import { Fragment, useEffect, useState, useRef } from "react";
import { DealsAPI, type Deal } from "../../api/deals";
import { DocumentsAPI, DOC_LABELS, VIDEO_DOC_TYPES, NO_EXCEL_TYPES, type DocumentType, type IntakeData } from "../../api/documents";
import IntakePanel from "./IntakePanel";
import { isDesktop } from "../../desktop/desktop";
import { autoPrintCreated, printDocument } from "../../desktop/autoPrint";
import { LABEL_SIZES, getLabelSize, setLabelSize, labelSizeKey } from "../../desktop/labelSize";
import { TimelineAPI } from "../../api/timeline";
import { getStages, type Stage, PipelinesAPI } from "../../api/pipelinesApi";
import { getProducts, type ProductDto } from "../../api/products";
import type { ClientDto } from "../../api/clients";
import type { UserDto } from "../../api/usersApi";
import { getCompanies, type CompanyDto } from "../../api/companies";
import { ProductSelectV2 } from "../ProductSelectV2";
import { ClientSelectSmart } from "../ClientSelectSmart";
import ClickToCall from "../Telephony/ClickToCall";
import CreateClientModal from "../clients/CreateClientModal";
import CreateContractModal from "../Contracts/CreateContractModal";
import CreateWorkOrderModal from "../WorkOrders/CreateWorkOrderModal";
import { WorkOrdersAPI, WorkOrderDto, STATUS_LABELS, STATUS_COLORS } from "../../api/workOrders";
import api from "../../api/axios";
import { useScanHandler } from "../../context/ScannerContext";
import { getPermissions, getRole, getUserId } from "../../hooks/usePermissions";
import DiscountBox, { type DiscountState } from "./DiscountBox";
import "../../styles/kanban/modal.css";

const PAY_LABELS: Record<string, { label: string; icon: string; color: string }> = {
  card:     { label: "Банковская карта", icon: "💳", color: "#6366f1" },
  kaspi:    { label: "Kaspi QR",         icon: "🟡", color: "#f59e0b" },
  halyk:    { label: "Halyk QR",         icon: "🟢", color: "#10b981" },
  transfer: { label: "Безнал",           icon: "🏦", color: "#60a5fa" },
  credit:   { label: "Кредит",           icon: "🏷️", color: "#a78bfa" },
};

const PAY_LIST = [
  { value: "cash",     label: "Наличные", icon: "💵", color: "#10b981" },
  { value: "card",     label: "Карта",    icon: "💳", color: "#6366f1" },
  { value: "kaspi",    label: "Kaspi QR", icon: "🟡", color: "#f59e0b" },
  { value: "halyk",    label: "Halyk QR", icon: "🟢", color: "#22c55e" },
  { value: "transfer", label: "Безнал",   icon: "🏦", color: "#60a5fa" },
  { value: "invoice",  label: "Счёт",     icon: "🧾", color: "#94a3b8" },
  { value: "credit",   label: "Кредит",   icon: "🏷️", color: "#a78bfa" },
];

const ALL_PAY_LABELS: Record<string, string> = {
  cash: "Наличные", card: "Банковская карта", kaspi: "Kaspi",
  halyk: "Halyk", credit: "Кредитная линия", invoice: "Счёт (безнал)",
  transfer: "Безнал",
};

interface Props {
  deal: Deal;
  clients: ClientDto[];
  users: UserDto[];
  onUpdated: () => void;
  onClose: () => void;
  /** Показывать секцию «Вернуть в работу» (для завершённых сделок) */
  showReturnToWork?: boolean;
}

function getCurrentUserName(): string {
  try {
    const token = localStorage.getItem('token');
    if (!token) return 'менеджер';
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.name || payload.username || payload.email || 'менеджер';
  } catch { return 'менеджер'; }
}

export const EditDealModal = ({ deal, clients, users, onUpdated, onClose, showReturnToWork }: Props) => {
  const [tab, setTab] = useState<"main" | "items" | "pay" | "docs" | "history" | "calls">("main");
  const [layout, setLayout] = useState<"top" | "side">(() => {
    try { return localStorage.getItem("dealModalLayout") === "side" ? "side" : "top"; } catch { return "top"; }
  });
  useEffect(() => { try { localStorage.setItem("dealModalLayout", layout); } catch { /* ignore */ } }, [layout]);
  const [showContractModal, setShowContractModal] = useState<'estimate' | 'contract' | 'act' | null>(null);
  const [showIntake, setShowIntake] = useState(false);
  const [showWorkOrder, setShowWorkOrder] = useState(false);
  const [dealWorkOrders, setDealWorkOrders] = useState<WorkOrderDto[]>([]);
  const loadDealWorkOrders = () => { WorkOrdersAPI.list({ dealId: deal.id }).then(setDealWorkOrders).catch(() => {}); };
  useEffect(() => { if (tab === "docs") loadDealWorkOrders(); }, [tab, deal.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const [title, setTitle]               = useState(deal.title);
  const [clientId, setClientId]         = useState(deal.clientId);
  const [responsibleId, setResponsibleId] = useState<number | null>(deal.responsibleUserId ?? null);
  const [currentStageId, setCurrentStageId] = useState(deal.stageId);

  const [products, setProducts]   = useState<any[]>([]);
  const [serials, setSerials]     = useState<Record<number, string[]>>({});
  const serialsRef = useRef(serials);
  serialsRef.current = serials;
  const [allProducts, setAllProducts] = useState<ProductDto[]>([]);
  const [documents, setDocuments] = useState<any[]>([]);
  const [timeline, setTimeline]   = useState<any[]>([]);
  const [dealCalls, setDealCalls] = useState<any[]>([]);
  const [audio, setAudio]         = useState<Record<number, string>>({});
  const [audioBusy, setAudioBusy] = useState<number | null>(null);
  const [stages, setStages]       = useState<Stage[]>([]);

  const [showCreateClient, setShowCreateClient] = useState(false);
  const [createClientPrefill, setCreateClientPrefill] = useState("");

  const [paymentMethod, setPaymentMethod] = useState<string>(deal.paymentMethod || "card");
  const [prepayment, setPrepayment]       = useState<string>(deal.prepayment ? String(deal.prepayment) : "");
  const [creditBalance, setCreditBalance] = useState<any>(null);
  const [creditLoading, setCreditLoading] = useState(false);
  const [creditOrderCreating, setCreditOrderCreating] = useState(false);
  const [dealDate, setDealDate]           = useState<string>(deal.dealDate ? deal.dealDate.slice(0, 10) : new Date().toISOString().slice(0, 10));
  const [companyId, setCompanyId]         = useState<number | null>((deal as any).companyId ?? null);
  const [companies, setCompanies]         = useState<CompanyDto[]>([]);
  const [companySearch, setCompanySearch] = useState<string>("");
  const [companyDropOpen, setCompanyDropOpen] = useState(false);
  const [keysText, setKeysText]           = useState<string>((deal.activationKeys ?? []).join("\n"));
  const [keysSaving, setKeysSaving]       = useState(false);
  const [montRequesting, setMontRequesting] = useState(false);
  const [waResending, setWaResending] = useState(false);
  const [waOpen, setWaOpen] = useState(false);
  const [waTemplates, setWaTemplates] = useState<{ name: string; label: string; params: number; urlButtons?: number }[]>([]);
  const [waTemplate, setWaTemplate] = useState('');
  const [waParams, setWaParams] = useState<string[]>([]);
  const [waUrlButtons, setWaUrlButtons] = useState<string[]>([]);
  const [waSendStatus, setWaSendStatus] = useState('');
  const [comment, setComment]   = useState("");
  const [disc, setDisc] = useState<DiscountState | null>(null);
  const loadDisc = () => { api.get(`/approvals/deal/${deal.id}`).then((r) => setDisc(r.data)).catch(() => {}); };
  const discChanged = async () => { loadDisc(); await reloadProducts(); await refreshAll(); onUpdated(); };

  // ── Соисполнители и запрос на удаление ───────────────────────
  const perms = getPermissions();
  const myRole = getRole();
  const canAssignCo = ["ADMIN", "OFFICE_LEAD", "SENIOR_OFFICE"].includes(myRole);
  const [coIds, setCoIds] = useState<number[]>(((deal as any).coExecutorIds ?? []).map(Number));
  const meId = getUserId();
  // техник открыл чужую (завершённую) сделку из карточки клиента — только просмотр
  const readOnly = myRole === "TECHNICIAN" && meId !== null && Number(deal.responsibleUserId) !== meId && !coIds.includes(meId);
  const [delReq, setDelReq] = useState<{ at: string; by: number | null; reason: string | null } | null>(
    (deal as any).deleteRequestedAt ? { at: (deal as any).deleteRequestedAt, by: (deal as any).deleteRequestedById ?? null, reason: (deal as any).deleteRequestReason ?? null } : null,
  );
  const toggleCo = async (userId: number) => {
    const next = coIds.includes(userId) ? coIds.filter((x) => x !== userId) : [...coIds, userId];
    try {
      const r = await DealsAPI.setCoExecutors(deal.id, next);
      setCoIds((r.data?.coExecutorIds ?? next).map(Number));
      setTimeline(await TimelineAPI.getByDeal(deal.id));
      onUpdated();
    } catch (e: any) {
      alert(e?.response?.data?.message ?? "Не удалось изменить соисполнителей");
    }
  };
  const [sending, setSending]   = useState(false);

  // ── Payments ──────────────────────────────────────────────────
  const [payments, setPayments]           = useState<any[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<string[]>([]);
  const [payLines, setPayLines]           = useState<{ method: string; amount: string }[]>([{ method: 'cash', amount: '' }]);
  const [payNote, setPayNote]             = useState('');
  const [payAdding, setPayAdding]         = useState(false);
  const [payFormOpen, setPayFormOpen]     = useState(false);
  const [receiptUrl, setReceiptUrl]       = useState<string | null>(deal.receiptUrl ?? null);

  // ── Return to work (для завершённых сделок) ──────────────────
  const [returnPipelineId, setReturnPipelineId] = useState<number | null>(deal.pipelineId);
  const [returnStageId, setReturnStageId]       = useState<number | null>(deal.stageId);
  const [returnStages, setReturnStages]         = useState<Stage[]>([]);
  const [returnSaving, setReturnSaving]         = useState(false);
  const [pipelines, setPipelines]               = useState<{ id: number; name: string }[]>([]);
  const timelineEndRef = useRef<HTMLDivElement>(null);
  const [promoInfo, setPromoInfo] = useState<any>(null);
  const [promoBusy, setPromoBusy] = useState(false);
  const loadPromo = () => {
    api.get(`/promotions/deal/${deal.id}`).then(r => setPromoInfo(r.data)).catch(() => setPromoInfo(null));
    api.get(`/loyalty/deal/${deal.id}`).then(r => setBonusInfo(r.data)).catch(() => setBonusInfo(null));
  };
  const [bonusInfo, setBonusInfo] = useState<any>(null);
  const [bonusAmt, setBonusAmt] = useState("");
  const [bonusBusy, setBonusBusy] = useState(false);

  const [docParamsType, setDocParamsType] = useState<DocumentType | null>(null);
  const [docParamsForm, setDocParamsForm] = useState({
    docNumber: "", contract: deal.title ?? "",
    docDate: new Date().toLocaleDateString("ru-RU"),
  });

  // ── Load ──────────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      const [list, dealRes, docs, tl, stageList, pipelineList, companiesList] = await Promise.all([
        getProducts(),
        DealsAPI.getById(deal.id),
        DocumentsAPI.getByDeal(deal.id),
        TimelineAPI.getByDeal(deal.id),
        getStages(deal.pipelineId),
        PipelinesAPI.getAll(),
        getCompanies(),
      ]);
      setCompanies(companiesList);
      setAllProducts(list);
      setProducts((dealRes.data.products ?? []).map((p: any) => ({
        ...p, price: Number(p.price), quantity: Number(p.quantity),
        product: list.find((x) => x.id === p.productId) ?? {},
      })));
      setDocuments(docs);
      setTimeline(tl);
      setStages(stageList);
      setPipelines(pipelineList);
      setReturnStages(stageList);

      // Загружаем платежи и методы оплаты воронки
      const [paymentsRes, pipelineRes] = await Promise.all([
        api.get(`/deals/${deal.id}/payments`).catch(() => ({ data: [] })),
        api.get(`/deals/pipelines/${deal.pipelineId}`).catch(() => ({ data: {} })),
      ]);
      setPayments(paymentsRes.data);
      const methods: string[] = pipelineRes.data?.paymentMethods ?? [];
      setPaymentMethods(methods);
      if (methods.length > 0) setPayLines(ls => ls.map(l => methods.includes(l.method) ? l : { ...l, method: methods[0] }));
    };
    load();
  }, [deal.id]);

  useEffect(() => {
    timelineEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [timeline]);

  useEffect(() => { loadDisc(); }, [deal.id, products.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Акции: пересчитываем подсказку при смене состава сделки и после оплат
  useEffect(() => { loadPromo(); }, [deal.id, products.length, payments.length]);

  // Загружаем кредитный баланс когда есть клиент (чтобы показать/скрыть метод оплаты)
  useEffect(() => {
    if (!clientId) { setCreditBalance(null); return; }
    setCreditLoading(true);
    api.get(`/credit/balance/${clientId}`)
      .then(r => setCreditBalance(r.data))
      .catch(() => setCreditBalance(null))
      .finally(() => setCreditLoading(false));
  }, [clientId]);

  // Если метод оплаты "кредит" выбран но клиент/товары не подходят — сбрасываем
  useEffect(() => {
    if (paymentMethod !== 'credit') return;
    if (!creditAvailable) setPaymentMethod('card');
  }, [creditBalance, products]);

  const refreshAll = async () => {
    const [docs, tl] = await Promise.all([
      DocumentsAPI.getByDeal(deal.id),
      TimelineAPI.getByDeal(deal.id),
    ]);
    setDocuments(docs);
    setTimeline(tl);
  };

  const loadSerials = () => { api.get(`/deals/${deal.id}/serials`).then((r) => setSerials(r.data ?? {})).catch(() => undefined); };
  useEffect(() => { loadSerials(); }, [deal.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const setSerialLocal = (pid: number, i: number, v: string, qty: number) =>
    setSerials((prev) => {
      const cur = Array.from({ length: qty }, (_, k) => prev[pid]?.[k] ?? "");
      cur[i] = v.replace(/\s+/g, "");
      return { ...prev, [pid]: cur };
    });
  const saveSerials = async (pid: number) => {
    try {
      const r = await api.put(`/deals/${deal.id}/serials`, { productId: pid, serials: serialsRef.current[pid] ?? [] });
      setSerials((prev) => ({ ...prev, [pid]: r.data.serials }));
    } catch (err: any) {
      const m = err?.response?.data?.message;
      alert(Array.isArray(m) ? m.join("\n") : m || "Не удалось сохранить серийные номера");
    }
  };
  const focusNextSn = () => {
    const el = Array.from(document.querySelectorAll<HTMLInputElement>("input[data-dsn]")).find((x) => !x.value.trim());
    el?.focus();
  };
  // код со сканера (телефон или USB): если курсор в поле серийного номера, он уходит туда
  useScanHandler((code) => {
    const a = document.activeElement as HTMLInputElement | null;
    if (!a || !a.hasAttribute("data-dsn")) return;
    const pid = Number(a.getAttribute("data-dsn"));
    const qty = products.filter((x: any) => Number(x.productId) === pid).reduce((s: number, x: any) => s + Number(x.quantity), 0);
    setSerialLocal(pid, Number(a.getAttribute("data-i")), code, qty);
    setTimeout(() => { saveSerials(pid); focusNextSn(); }, 80);
  }, tab === "items");

  const reloadProducts = async () => {
    loadSerials();
    const dealRes = await DealsAPI.getById(deal.id);
    setProducts((dealRes.data.products ?? []).map((p: any) => ({
      ...p, price: Number(p.price), quantity: Number(p.quantity),
      product: allProducts.find((x) => x.id === p.productId) ?? {},
    })));
  };

  // ── Stage move ───────────────────────────────────────────────
  const moveToStage = async (stage: Stage) => {
    await DealsAPI.updateStage(deal.id, { pipelineId: deal.pipelineId, stageId: stage.id });
    await TimelineAPI.add({ dealId: deal.id, type: "status", message: `Статус изменён → ${stage.name}` });
    setCurrentStageId(stage.id);
    const tl = await TimelineAPI.getByDeal(deal.id);
    setTimeline(tl);
    onUpdated();
  };

  // ── Timeline comment ──────────────────────────────────────────
  const sendComment = async () => {
    if (!comment.trim()) return;
    setSending(true);
    try {
      await TimelineAPI.add({ dealId: deal.id, type: "comment", message: comment.trim() });
      setComment("");
      const tl = await TimelineAPI.getByDeal(deal.id);
      setTimeline(tl);
    } finally {
      setSending(false);
    }
  };

  // ── Product actions ───────────────────────────────────────────
  const addProduct = async (productId: number) => {
    const p = allProducts.find((x) => x.id === productId);
    if (!p) return;
    await DealsAPI.addProduct(deal.id, { productId: p.id, quantity: 1, price: Number(p.salePrice) });
    await reloadProducts();
  };

  const updateQty = async (id: number, qty: number) => {
    if (qty < 1) return;
    const dp = products.find((x) => x.id === id);
    if (!dp) return;
    await DealsAPI.updateProduct(id, { quantity: qty, price: dp.price });
    await reloadProducts();
  };

  const updatePriceLocal = (id: number, price: number) =>
    setProducts((prev) => prev.map((p) => (p.id === id ? { ...p, price } : p)));

  const updatePriceServer = async (id: number) => {
    const dp = products.find((x) => x.id === id);
    if (!dp) return;
    await DealsAPI.updateProduct(id, { quantity: dp.quantity, price: dp.price });
  };

  const removeProduct = async (id: number) => {
    await DealsAPI.removeProduct(id);
    await reloadProducts();
  };

  const productsSum = products.reduce((s, p) => s + p.price * p.quantity, 0);
  // скидка на весь чек (назначена админом или согласована им) вычитается из суммы позиций
  const totalAmount = productsSum > 0 ? Math.max(0, productsSum - (disc?.discount ?? 0)) : 0;
  // «Оплачено»: внесённые оплаты; если их меньше указанного аванса — считаем оплаченным аванс
  const paidTotal = Math.max(
    payments.reduce((s: number, p: any) => s + Number(p.amount), 0),
    Number(prepayment) || 0,
  );

  // ── Documents ────────────────────────────────────────────────
  const createDocument = async (type: DocumentType) => {
    const existing = documents.find((d) => d.type === type);
    if (existing) {
      const ok = window.confirm(`Документ "${DOC_LABELS[type]}" уже существует. Заменить?`);
      if (!ok) return;
    }
    if (type === "intake") { setShowIntake(true); return; }
    if (type === "avr" || type === "waybill" || type === "avr_pc") { setDocParamsType(type); return; }
    await doGenerate(type);
  };

  const doGenerate = async (type: DocumentType, opts?: any) => {
    const newDoc = await DocumentsAPI.generate(deal.id, type, opts);
    await TimelineAPI.add({ dealId: deal.id, type: "document", message: `Документ "${DOC_LABELS[type]}" создан`, documentId: newDoc.id });
    await refreshAll();
    // десктоп: автопечать по правилам (акт приёма → ещё и этикетка)
    if (isDesktop) autoPrintCreated(deal.id, type === "intake" ? ["intake", "intake_label"] : [type]);
  };

  // ── Save ─────────────────────────────────────────────────────
  const save = async () => {
    await DealsAPI.update(deal.id, { title, clientId, responsibleUserId: responsibleId ?? undefined, paymentMethod, prepayment: prepayment ? Number(prepayment) : 0, dealDate, companyId: companyId ?? undefined } as any);

    // Если выбран кредит и кредит-заказа ещё нет — создаём автоматически
    if (paymentMethod === 'credit' && deal.paymentMethod !== 'credit') {
      try {
        await api.post(`/credit/orders/from-deal/${deal.id}`);
      } catch (e: any) {
        alert(e?.response?.data?.message ?? 'Сделка сохранена, но кредитный заказ не создан: ' + (e?.message ?? ''));
      }
    }

    onUpdated();
    onClose();
  };

  // ── Credit availability check ────────────────────────────────
  const creditAvailable = (() => {
    const acc = creditBalance?.account;
    if (!acc || !acc.isActive) return false;
    if (creditBalance.overdueOrders?.length > 0) return false;
    if (!products.length) return false;
    // Проверяем разрешённые товары/категории
    if (acc.allowedProductIds?.length) {
      const allowed = acc.allowedProductIds.map(Number);
      if (!products.every((p: any) => allowed.includes(Number(p.productId)))) return false;
    }
    if (acc.allowedCategoryIds?.length) {
      const allowed = acc.allowedCategoryIds.map(Number);
      if (!products.every((p: any) => allowed.includes(Number(p.product?.categoryId ?? p.product?.category?.id)))) return false;
    }
    return true;
  })();

  // ── Helpers ──────────────────────────────────────────────────
  const client = clients.find((c) => c.id === clientId);
  const pay = paymentMethod ? PAY_LABELS[paymentMethod] : null;
  const currentStage = stages.find((s) => s.id === currentStageId);
  const fmtDate = (s: string) => new Date(s).toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
  const fmtMoney = (n: number) => new Intl.NumberFormat("ru-RU").format(n) + " ₸";
  const calls = timeline.filter((e: any) => e.type === "call");
  useEffect(() => {
    if (tab !== "calls" || !deal?.id) return undefined;
    let alive = true;
    api.get(`/telephony/deal-calls/${deal.id}`).then((r) => { if (alive) setDealCalls(r.data); }).catch(() => { if (alive) setDealCalls([]); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, deal?.id, timeline.length]);
  const tabItems = [
    ["main", "📋 Обзор"],
    ["items", `📦 Товары${products.length ? ` (${products.length})` : ""}`],
    ["pay", `💰 Оплата${payments.length ? ` (${payments.length})` : ""}`],
    ["docs", `📄 Документы${documents.length ? ` (${documents.length})` : ""}`],
    ["calls", `📞 Звонки${calls.length ? ` (${calls.length})` : ""}`],
    ["history", `🕘 История${timeline.length ? ` (${timeline.length})` : ""}`],
  ] as const;
  // на телефоне боковое меню не нужно — всегда верхнее
  const sideNav = layout === "side" && typeof window !== "undefined" && window.innerWidth > 768;
  const tlIcon: Record<string, string> = { system: "⚙️", comment: "💬", document: "📄", payment: "💰", status: "🔄", wa_send: "📲", call: "📞" };

  return (
    <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="edit-deal-modal" style={{
        background: "#0f1117", border: "1px solid #1f2937", borderRadius: 14,
        width: "min(1100px, 96vw)", height: "min(88vh, 820px)", maxHeight: "92vh",
        display: "flex", flexDirection: "column",
        boxShadow: "0 24px 80px rgba(0,0,0,0.7)",
        overflow: "hidden",
      }}>

        {/* ── Header ── */}
        <div style={{ padding: "14px 20px", borderBottom: "1px solid #1f2937", flexShrink: 0 }}>
          {/* Строка с табами и закрытием — всегда сверху */}
          <div className="edit-deal-header-row" style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
            <span style={{ fontSize: 12, color: "#6b7280", fontWeight: 600, flexShrink: 0 }}>{deal.number}</span>
            <div style={{ flex: 1, minWidth: 4 }} />
            <div style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
              <button
                onClick={() => setLayout((l) => (l === "side" ? "top" : "side"))}
                title="Переключить вид меню: сверху / сбоку"
                className="edit-deal-layout-toggle"
                style={{ background: "rgba(255,255,255,0.05)", border: "1px solid #374151", color: "#9ca3af", borderRadius: 7, fontSize: 12, padding: "4px 10px", cursor: "pointer" }}
              >
                {layout === "side" ? "⬒ Меню сверху" : "◧ Меню сбоку"}
              </button>
              <button onClick={onClose} style={{ background: "none", border: "none", color: "#6b7280", fontSize: 20, cursor: "pointer", lineHeight: 1, padding: "0 0 0 6px", flexShrink: 0 }}>✕</button>
            </div>
          </div>
          {/* Бейджи — переносятся на мобильном */}
          <div className="edit-deal-badges">
            {pay ? (
              <span style={{ fontSize: 12, padding: "2px 10px", borderRadius: 20, background: pay.color + "22", color: pay.color, fontWeight: 700, border: `1px solid ${pay.color}44` }}>
                {pay.icon} {pay.label}
              </span>
            ) : (
              <span style={{ fontSize: 12, padding: "2px 10px", borderRadius: 20, background: "rgba(107,114,128,0.12)", color: "#4b5563", border: "1px solid #1f2937" }}>
                Оплата не выбрана
              </span>
            )}
            <span style={{ fontSize: 12, padding: "2px 10px", borderRadius: 20, background: "rgba(99,102,241,0.12)", color: "#818cf8", fontWeight: 600 }}>
              {fmtMoney(totalAmount || Number(deal.amount))}
            </span>
            {Number(prepayment) > 0 && (
              <span style={{ fontSize: 12, padding: "2px 10px", borderRadius: 20, background: "rgba(16,185,129,0.12)", color: "#10b981", fontWeight: 600 }}>
                аванс {fmtMoney(Number(prepayment))}
              </span>
            )}
            {currentStage && (
              <span style={{ fontSize: 12, padding: "2px 10px", borderRadius: 20, background: (currentStage.color || "#6366f1") + "22", color: currentStage.color || "#818cf8", border: `1px solid ${(currentStage.color || "#6366f1")}44` }}>
                {currentStage.name}
              </span>
            )}
          </div>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            style={{ background: "none", border: "none", outline: "none", color: "#f9fafb", fontSize: 17, fontWeight: 700, width: "100%" }}
          />
        </div>

        {/* ── Payment / Keys status banner ── */}
        {(deal.paidAt || (deal.activationKeys?.length ?? 0) > 0 || deal.status === 'won' || deal.paymentMethod === 'credit') && (
          <div style={{ display: 'flex', gap: 8, padding: '8px 20px', background: '#0d1117', borderBottom: '1px solid #1f2937', flexWrap: 'wrap' }}>
            {deal.paidAt ? (
              <span style={{ fontSize: 12, padding: '3px 10px', borderRadius: 20, background: 'rgba(16,185,129,0.12)', color: '#10b981', border: '1px solid rgba(16,185,129,0.3)', fontWeight: 600 }}>
                💳 Оплачено {new Date(deal.paidAt).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
              </span>
            ) : deal.paymentMethod === 'credit' ? (
              <span style={{ fontSize: 12, padding: '3px 10px', borderRadius: 20, background: 'rgba(167,139,250,0.12)', color: '#a78bfa', border: '1px solid rgba(167,139,250,0.3)', fontWeight: 600 }}>
                🏷️ Кредитный заказ
              </span>
            ) : deal.paymentMethod === 'card' ? (
              <span style={{ fontSize: 12, padding: '3px 10px', borderRadius: 20, background: 'rgba(245,158,11,0.12)', color: '#f59e0b', border: '1px solid rgba(245,158,11,0.3)', fontWeight: 600 }}>
                ⏳ Ожидаем оплату
              </span>
            ) : null}

            {(deal.activationKeys?.length ?? 0) > 0 ? (
              <span style={{ fontSize: 12, padding: '3px 10px', borderRadius: 20, background: 'rgba(99,102,241,0.12)', color: '#818cf8', border: '1px solid rgba(99,102,241,0.3)', fontWeight: 600 }}>
                🔑 Ключи получены ({deal.activationKeys!.length} шт.)
              </span>
            ) : (deal.paidAt || deal.paymentMethod === 'credit') ? (
              <>
                <span style={{ fontSize: 12, padding: '3px 10px', borderRadius: 20, background: 'rgba(239,68,68,0.12)', color: '#f87171', border: '1px solid rgba(239,68,68,0.3)', fontWeight: 600 }}>
                  ⚠️ Ключи не получены
                </span>
                <button
                  disabled={montRequesting}
                  onClick={async () => {
                    setMontRequesting(true);
                    try {
                      await DealsAPI.requestMontKeys(deal.id);
                      await refreshAll();
                      onUpdated();
                    } finally {
                      setMontRequesting(false);
                    }
                  }}
                  style={{ fontSize: 12, padding: '3px 12px', borderRadius: 20, background: 'rgba(99,102,241,0.15)', color: '#818cf8', border: '1px solid rgba(99,102,241,0.4)', fontWeight: 600, cursor: 'pointer', opacity: montRequesting ? 0.6 : 1 }}
                >
                  {montRequesting ? '⏳ Запрашиваем...' : '🔄 Запросить ключи у MONT'}
                </button>
              </>
            ) : null}

            {deal.status === 'won' && (
              <span style={{ fontSize: 12, padding: '3px 10px', borderRadius: 20, background: 'rgba(16,185,129,0.12)', color: '#10b981', border: '1px solid rgba(16,185,129,0.3)', fontWeight: 600 }}>
                ✅ Сделка завершена
              </span>
            )}
            {deal.status === 'lost' && (
              <span style={{ fontSize: 12, padding: '3px 10px', borderRadius: 20, background: 'rgba(239,68,68,0.12)', color: '#f87171', border: '1px solid rgba(239,68,68,0.3)', fontWeight: 600 }}>
                ❌ Сделка проиграна
              </span>
            )}
          </div>
        )}

        {readOnly && (
          <div style={{ padding: "8px 20px", background: "rgba(245,158,11,0.1)", borderBottom: "1px solid rgba(245,158,11,0.3)", color: "#fbbf24", fontSize: 13, flexShrink: 0 }}>
            👁 Режим просмотра: это не ваша сделка. Изменения недоступны.
          </div>
        )}

        {/* ── Меню разделов (верхнее) ── */}
        {!sideNav && (
        <div className="edit-deal-tabs" style={{ display: "flex", gap: 4, padding: "8px 20px 0", borderBottom: "1px solid #1f2937", flexShrink: 0, overflowX: "auto", background: "#0d1017" }}>
          {tabItems.map(([key, label]) => (
            <button key={key} onClick={() => setTab(key)} style={{
              padding: "9px 16px", border: "none", cursor: "pointer", fontSize: 13, whiteSpace: "nowrap", flexShrink: 0,
              fontWeight: tab === key ? 700 : 500, borderRadius: "9px 9px 0 0",
              background: tab === key ? "#0f1117" : "transparent",
              color: tab === key ? "#a5b4fc" : "#6b7280",
              borderBottom: tab === key ? "2px solid #6366f1" : "2px solid transparent",
            }}>{label}</button>
          ))}
        </div>
        )}

        {/* ── WA ручная отправка (только в обзоре) ── */}
        {tab === "main" && (
        <div className="edit-deal-wa" style={{ padding: '6px 20px', borderBottom: '1px solid #1f2937', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button
            onClick={async () => {
              if (!waOpen) {
                const tpls = (await api.get('/notifications/wa-templates')).data;
                setWaTemplates(tpls);
                const halykCode = (deal as any).halykOrderCode ?? '';
                if (tpls.length > 0) {
                  const tpl = tpls[0];
                  setWaTemplate(tpl.name);
                  // Автозаполнение param1 кодом заказа Halyk если он есть
                  const params = Array(tpl.params).fill('');
                  if (halykCode && params.length >= 1) params[0] = halykCode;
                  setWaParams(params);
                  // Автозаполнение URL кнопки суффиксом страницы заказа
                  const urls = Array(tpl.urlButtons ?? 0).fill('');
                  if (halykCode && urls.length >= 1) urls[0] = `ru/halyk-order/${halykCode}`;
                  setWaUrlButtons(urls);
                }
                setWaSendStatus('');
              }
              setWaOpen(p => !p);
            }}
            style={{ alignSelf: 'flex-start', fontSize: 12, padding: '3px 12px', borderRadius: 20, background: 'rgba(16,185,129,0.12)', color: '#10b981', border: '1px solid rgba(16,185,129,0.3)', fontWeight: 600, cursor: 'pointer' }}
          >
            💬 WA уведомление
          </button>
          {waOpen && (
            <div style={{ background: '#141414', border: '1px solid #2a2a2a', borderRadius: 10, padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {waTemplates.length === 0 ? (
                <span style={{ fontSize: 12, color: '#888' }}>Нет шаблонов. Добавьте в Настройки → Уведомления → WhatsApp.</span>
              ) : (
                <>
                  <select
                    value={waTemplate}
                    onChange={e => {
                      const tpl = waTemplates.find(t => t.name === e.target.value);
                      const halykCode = (deal as any).halykOrderCode ?? '';
                      setWaTemplate(e.target.value);
                      const params = Array(tpl?.params ?? 0).fill('');
                      if (halykCode && params.length >= 1) params[0] = halykCode;
                      setWaParams(params);
                      const urls = Array(tpl?.urlButtons ?? 0).fill('');
                      if (halykCode && urls.length >= 1) urls[0] = `ru/halyk-order/${halykCode}`;
                      setWaUrlButtons(urls);
                      setWaSendStatus('');
                    }}
                    style={{ fontSize: 13, padding: '4px 8px', borderRadius: 6, background: '#252525', color: '#ddd', border: '1px solid #3a3a3a', outline: 'none' }}
                  >
                    {waTemplates.map(t => (
                      <option key={t.name} value={t.name}>{t.label} ({t.name})</option>
                    ))}
                  </select>
                  {waParams.map((v, i) => (
                    <input
                      key={`p${i}`}
                      value={v}
                      onChange={e => setWaParams(p => p.map((x, j) => j === i ? e.target.value : x))}
                      placeholder={`Параметр {{${i + 1}}}`}
                      style={{ fontSize: 13, padding: '4px 8px', borderRadius: 6, background: '#252525', color: '#ddd', border: '1px solid #3a3a3a', outline: 'none' }}
                    />
                  ))}
                  {waUrlButtons.map((v, i) => (
                    <input
                      key={`u${i}`}
                      value={v}
                      onChange={e => setWaUrlButtons(p => p.map((x, j) => j === i ? e.target.value : x))}
                      placeholder={`URL кнопка ${i + 1} (суффикс)`}
                      style={{ fontSize: 13, padding: '4px 8px', borderRadius: 6, background: '#1e1e2e', color: '#a78bfa', border: '1px solid rgba(99,102,241,0.4)', outline: 'none' }}
                    />
                  ))}
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <button
                      disabled={waResending}
                      onClick={async () => {
                        setWaResending(true); setWaSendStatus('');
                        try {
                          const data = (await api.post('/notifications/send-wa', { dealId: deal.id, template: waTemplate, params: waParams, urlButtons: waUrlButtons.filter(Boolean) })).data;
                          setWaSendStatus(data.ok ? `✅ Отправлено на ${data.phone}` : '❌ Ошибка отправки');
                        } catch { setWaSendStatus('❌ Ошибка'); }
                        finally { setWaResending(false); }
                      }}
                      style={{ fontSize: 12, padding: '4px 14px', borderRadius: 6, background: '#10b981', color: '#fff', border: 'none', fontWeight: 600, cursor: waResending ? 'not-allowed' : 'pointer', opacity: waResending ? 0.6 : 1 }}
                    >
                      {waResending ? '⏳ Отправка...' : 'Отправить'}
                    </button>
                    <button onClick={() => setWaOpen(false)} style={{ fontSize: 12, padding: '4px 10px', borderRadius: 6, background: 'transparent', color: '#888', border: '1px solid #333', cursor: 'pointer' }}>
                      Закрыть
                    </button>
                    {waSendStatus && <span style={{ fontSize: 12, color: waSendStatus.startsWith('✅') ? '#a3e635' : '#f87171' }}>{waSendStatus}</span>}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
        )}

        {/* ── Stage bar ── */}
        {tab === "main" && stages.length > 0 && (
          <div className="edit-deal-stages" style={{ display: "flex", borderBottom: "1px solid #1f2937", flexShrink: 0, overflowX: "auto", padding: "0 20px" }}>
            {stages.map((s, i) => {
              const isActive = s.id === currentStageId;
              const color = s.color || "#6366f1";
              return (
                <button key={s.id} onClick={() => moveToStage(s)} style={{
                  padding: "8px 14px", border: "none", cursor: "pointer", fontSize: 12,
                  fontWeight: isActive ? 700 : 400, background: "none",
                  color: isActive ? color : "#4b5563",
                  borderBottom: isActive ? `2px solid ${color}` : "2px solid transparent",
                  whiteSpace: "nowrap", transition: "all 0.15s", flexShrink: 0,
                }}>
                  {i + 1}. {s.name}
                </button>
              );
            })}
          </div>
        )}

        {/* ── Body ── */}
        <div className="edit-deal-body" style={{ flex: 1, overflow: "hidden", display: "flex", minHeight: 0 }}>

          {/* ── Боковое меню разделов ── */}
          {sideNav && (
            <div style={{ width: 200, flexShrink: 0, borderRight: "1px solid #1f2937", background: "#0d1017", display: "flex", flexDirection: "column", padding: "12px 10px", gap: 4, overflowY: "auto" }}>
              {tabItems.map(([key, label]) => (
                <button key={key} onClick={() => setTab(key)} style={{
                  textAlign: "left", padding: "10px 12px", border: "none", cursor: "pointer", fontSize: 13, borderRadius: 8,
                  fontWeight: tab === key ? 700 : 500,
                  background: tab === key ? "rgba(99,102,241,0.16)" : "transparent",
                  color: tab === key ? "#c7d2fe" : "#9ca3af",
                  borderLeft: tab === key ? "3px solid #6366f1" : "3px solid transparent",
                }}>{label}</button>
              ))}
              <div style={{ marginTop: "auto", paddingTop: 12, borderTop: "1px solid #1f2937", display: "flex", flexDirection: "column", gap: 6, fontSize: 12 }}>
                {(() => {
                  const total = totalAmount || Number(deal.amount) || 0;
                  const paid = paidTotal;
                  return (
                    <>
                      <div style={{ display: "flex", justifyContent: "space-between", color: "#6b7280" }}>Сумма <b style={{ color: "#c084fc" }}>{fmtMoney(total)}</b></div>
                      <div style={{ display: "flex", justifyContent: "space-between", color: "#6b7280" }}>Оплачено <b style={{ color: "#10b981" }}>{fmtMoney(paid)}</b></div>
                      <div style={{ display: "flex", justifyContent: "space-between", color: "#6b7280" }}>Остаток <b style={{ color: total - paid > 0 ? "#f59e0b" : "#10b981" }}>{fmtMoney(Math.max(0, total - paid))}</b></div>
                    </>
                  );
                })()}
              </div>
            </div>
          )}

          {(tab === "main" || tab === "items" || tab === "pay") && (
            <>
              <div className="edit-deal-left" style={{ flex: 1, overflowY: "auto", overflowX: "hidden", padding: "18px 20px", display: "flex", flexDirection: "column", gap: 16, minWidth: 0 }}>

                {tab === "main" && delReq && (
                  <div style={{ padding: "10px 14px", borderRadius: 10, background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.35)", display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                    <span style={{ fontSize: 13, color: "#fca5a5", flex: 1, minWidth: 200 }}>
                      🗑 Запрошено удаление сделки{delReq.reason ? `: ${delReq.reason}` : ""}
                    </span>
                    {perms.canDeleteDeals && (
                      <>
                        <button style={{ ...priBtn, background: "#ef4444", padding: "6px 14px", fontSize: 12 }} onClick={async () => {
                          if (!window.confirm("Одобрить и удалить сделку?")) return;
                          try { await DealsAPI.approveDelete(deal.id); onUpdated(); onClose(); } catch (e: any) { alert(e?.response?.data?.message ?? "Не удалось удалить"); }
                        }}>Одобрить удаление</button>
                        <button style={{ ...secBtn, padding: "6px 14px", fontSize: 12 }} onClick={async () => {
                          try { await DealsAPI.rejectDelete(deal.id); setDelReq(null); setTimeline(await TimelineAPI.getByDeal(deal.id)); onUpdated(); } catch (e: any) { alert(e?.response?.data?.message ?? "Ошибка"); }
                        }}>Отклонить</button>
                      </>
                    )}
                  </div>
                )}

                {tab === "main" && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 }}>
                  {(() => {
                    const total = totalAmount || Number(deal.amount) || 0;
                    const paid = paidTotal;
                    const cells: [string, string, string][] = [
                      ["Сумма сделки", fmtMoney(total), "#c084fc"],
                      [payments.length === 0 && Number(prepayment) > 0 ? "Оплачено (аванс)" : "Оплачено", fmtMoney(paid), "#10b981"],
                      ["К оплате", fmtMoney(Math.max(0, total - paid)), total - paid > 0 ? "#f59e0b" : "#10b981"],
                      ["Позиций", String(products.length), "#93c5fd"],
                    ];
                    return cells.map(([l, v, c]) => (
                      <div key={l} style={{ ...cardSt, padding: "10px 14px" }}>
                        <div style={{ fontSize: 11, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em" }}>{l}</div>
                        <div style={{ fontSize: 18, fontWeight: 700, color: c, marginTop: 2 }}>{v}</div>
                      </div>
                    ));
                  })()}
                </div>
                )}

                {tab === "main" && (
                <section style={{ ...cardSt, order: 0 }}>
                  <SectionTitle>Информация</SectionTitle>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: 12 }}>
                    <div style={{ gridColumn: "1 / -1" }}>
                    <Field label="Клиент">
                      <ClientSelectSmart clients={clients} value={clientId}
                        onSelect={(c) => setClientId(c.id)}
                        onCreateNew={(p) => { setCreateClientPrefill(p); setShowCreateClient(true); }} />
                    </Field>
                    </div>
                    <Field label="Ответственный">
                      <select style={inputSt} value={responsibleId ?? ""} onChange={(e) => setResponsibleId(Number(e.target.value) || null)}>
                        <option value="">Не назначен</option>
                        {users.map((u) => <option key={u.id} value={u.id}>{u.firstName} {u.lastName}</option>)}
                      </select>
                    </Field>
                    <Field label="Компания (ЮЛ)">
                      <div style={{ position: "relative" }}>
                        {companyId && !companyDropOpen ? (
                          <div
                            style={{ ...inputSt, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6 }}
                            onClick={() => { setCompanySearch(""); setCompanyDropOpen(true); }}
                          >
                            <span style={{ color: "#c7d2fe" }}>{companies.find(c => c.id === companyId)?.name ?? "—"}</span>
                            <span style={{ color: "#6b7280", fontSize: 11 }}>изменить</span>
                          </div>
                        ) : (
                          <input
                            style={inputSt}
                            placeholder="Поиск по названию или БИН..."
                            value={companySearch}
                            onChange={e => { setCompanySearch(e.target.value); setCompanyDropOpen(true); }}
                            onFocus={() => setCompanyDropOpen(true)}
                            onBlur={() => setTimeout(() => setCompanyDropOpen(false), 200)}
                            autoComplete="off"
                          />
                        )}
                        {companyDropOpen && (
                          <div style={{ position: "absolute", zIndex: 9999, width: "100%", top: "100%", background: "#1e293b", border: "1px solid #334155", borderRadius: 6, maxHeight: 180, overflowY: "auto" }}>
                            {companyId && (
                              <div
                                style={{ padding: "7px 10px", fontSize: 12, color: "#f87171", cursor: "pointer", borderBottom: "1px solid #334155" }}
                                onMouseDown={e => { e.preventDefault(); setCompanyId(null); setCompanyDropOpen(false); setCompanySearch(""); }}
                              >
                                Очистить
                              </div>
                            )}
                            {companies
                              .filter(c => !companySearch || c.name.toLowerCase().includes(companySearch.toLowerCase()) || c.bin?.includes(companySearch))
                              .map(c => (
                                <div
                                  key={c.id}
                                  style={{ padding: "7px 10px", fontSize: 13, cursor: "pointer", color: "#e2e8f0" }}
                                  onMouseDown={e => { e.preventDefault(); setCompanyId(c.id); setCompanyDropOpen(false); setCompanySearch(""); }}
                                >
                                  {c.name}
                                  {c.bin && <span style={{ color: "#6b7280", fontSize: 11, marginLeft: 6 }}>{c.bin}</span>}
                                </div>
                              ))}
                            {companies.filter(c => !companySearch || c.name.toLowerCase().includes(companySearch.toLowerCase()) || c.bin?.includes(companySearch)).length === 0 && (
                              <div style={{ padding: "8px 10px", color: "#64748b", fontSize: 12 }}>Не найдено</div>
                            )}
                          </div>
                        )}
                      </div>
                    </Field>
                  </div>

                  {client && (
                    <div style={{ marginTop: 10, padding: "10px 13px", borderRadius: 10, background: "#131926", border: "1px solid #1f2937", display: "flex", gap: 10, alignItems: "center" }}>
                      <div style={{ width: 34, height: 34, borderRadius: "50%", background: "#6366f1", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, color: "#fff", fontSize: 14, flexShrink: 0 }}>
                        {(client.firstName?.[0] ?? "?").toUpperCase()}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 600, color: "#f9fafb", fontSize: 13 }}>{client.firstName} {client.lastName}</div>
                        <div style={{ color: "#6b7280", fontSize: 12, display: "flex", alignItems: "center", gap: 4 }}>
                          {client.phone}
                          {client.phone && <ClickToCall phone={client.phone} />}
                        </div>
                        {client.email && <div style={{ color: "#6b7280", fontSize: 12 }}>{client.email}</div>}
                      </div>
                    </div>
                  )}

                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: 12, marginTop: 12 }}>
                  {/* Дата сделки */}
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase" as const, letterSpacing: "0.06em", color: "#6b7280", marginBottom: 6 }}>Дата сделки</div>
                    <input
                      type="date"
                      value={dealDate}
                      onChange={e => setDealDate(e.target.value)}
                      style={{ background: "#1a2030", border: "1px solid #374151", borderRadius: 7, color: "#f9fafb", fontSize: 13, padding: "8px 10px", outline: "none", width: "100%", boxSizing: "border-box" as const, colorScheme: "dark" }}
                    />
                  </div>
                  </div>

                  {/* Соисполнители */}
                  <div style={{ marginTop: 14 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase" as const, letterSpacing: "0.06em", color: "#6b7280", marginBottom: 6 }}>
                      Соисполнители {canAssignCo ? "" : "(назначает руководитель)"}
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {users
                        .filter((u: any) => u.isActive !== false && u.id !== responsibleId)
                        .map((u: any) => {
                          const on = coIds.includes(u.id);
                          if (!canAssignCo && !on) return null;
                          return (
                            <button key={u.id} type="button" disabled={!canAssignCo} onClick={() => toggleCo(u.id)}
                              style={{
                                padding: "4px 11px", borderRadius: 14, fontSize: 12, fontWeight: 600, cursor: canAssignCo ? "pointer" : "default",
                                border: `1px solid ${on ? "#6366f1" : "#374151"}`,
                                background: on ? "rgba(99,102,241,0.18)" : "transparent",
                                color: on ? "#c7d2fe" : "#9ca3af",
                              }}>
                              {on ? "✓ " : "+ "}{u.lastName} {u.firstName}
                            </button>
                          );
                        })}
                      {!canAssignCo && coIds.length === 0 && <span style={{ color: "#4b5563", fontSize: 12 }}>не назначены</span>}
                    </div>
                  </div>
                </section>
                )}

                {tab === "pay" && (
                <section style={{ ...cardSt, order: 0 }}>
                  <SectionTitle>Оплата сделки</SectionTitle>
                  {/* Предоплата */}
                  <div style={{ maxWidth: 360 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase" as const, letterSpacing: "0.06em", color: "#6b7280", marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
                      Предоплата
                      {totalAmount > 0 && [30, 50, 100].map((pc) => (
                        <button key={pc} type="button" onClick={() => setPrepayment(String(Math.round(totalAmount * pc / 100)))}
                          style={{ padding: "0 7px", borderRadius: 6, border: "1px solid #374151", background: "transparent", color: "#9ca3af", fontSize: 10, cursor: "pointer", fontWeight: 600 }}>
                          {pc}%
                        </button>
                      ))}
                    </div>
                    <div style={{ position: "relative" }}>
                      <input
                        type="number"
                        placeholder="0"
                        value={prepayment}
                        onChange={e => setPrepayment(e.target.value)}
                        style={{ background: "#1a2030", border: "1px solid #374151", borderRadius: 7, color: "#f9fafb", fontSize: 13, padding: "8px 10px", outline: "none", width: "100%", boxSizing: "border-box" as const }}
                      />
                      {Number(prepayment) > 0 && totalAmount > 0 && (
                        <div style={{ marginTop: 4, fontSize: 11, color: "#10b981" }}>
                          Остаток к оплате: {fmtMoney(totalAmount - Number(prepayment))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* ── Оплаты ── */}
                  <div style={{ marginTop: 14 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase" as const, letterSpacing: "0.06em", color: "#6b7280" }}>Внесённые оплаты</div>
                      <button onClick={() => setPayFormOpen(o => !o)}
                        style={{ fontSize: 11, padding: '2px 10px', borderRadius: 12, background: 'rgba(16,185,129,0.12)', color: '#10b981', border: '1px solid rgba(16,185,129,0.25)', cursor: 'pointer', fontWeight: 600 }}>
                        {payFormOpen ? '✕ Отмена' : '+ Добавить'}
                      </button>
                    </div>

                    {payments.length > 0 && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 8 }}>
                        {payments.map((pmt: any) => (
                          <div key={pmt.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', borderRadius: 8, background: '#0d1117', border: '1px solid #1f2937' }}>
                            <span style={{ fontSize: 13, fontWeight: 700, color: '#10b981' }}>{Number(pmt.amount).toLocaleString('ru-RU')} ₸</span>
                            <span style={{ fontSize: 12, color: '#6b7280', flex: 1 }}>{ALL_PAY_LABELS[pmt.method] ?? pmt.method}</span>
                            {pmt.note && <span style={{ fontSize: 11, color: '#4b5563' }}>{pmt.note}</span>}
                            <button onClick={async () => {
                              await api.delete(`/deals/${deal.id}/payments/${pmt.id}`, { data: { deletedBy: getCurrentUserName() } });
                              const res = await api.get(`/deals/${deal.id}/payments`);
                              setPayments(res.data);
                              const tl = await TimelineAPI.getByDeal(deal.id);
                              setTimeline(tl);
                            }} style={{ background: 'none', border: 'none', color: '#4b5563', cursor: 'pointer', fontSize: 14, padding: 0 }}>×</button>
                          </div>
                        ))}
                        <div style={{ textAlign: 'right', fontSize: 12, color: '#10b981', fontWeight: 700 }}>
                          Итого оплачено: {payments.reduce((s: number, p: any) => s + Number(p.amount), 0).toLocaleString('ru-RU')} ₸
                        </div>
                      </div>
                    )}

                    {bonusInfo && bonusInfo.enabled && !bonusInfo.clientId && (
                      <div style={{ marginBottom: 8, padding: '8px 12px', background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.3)', borderRadius: 8, fontSize: 12, color: '#9ca3af' }}>
                        🪙 Бонусы: укажите клиента в сделке, тогда за оплату начислятся бонусы ({bonusInfo.accrualPercent}%), а накопленные можно будет списать.
                      </div>
                    )}
                    {bonusInfo && bonusInfo.enabled && bonusInfo.clientId && (
                      <div style={{ marginBottom: 8, padding: '8px 12px', background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.3)', borderRadius: 8, fontSize: 12, color: '#d1d5db', display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <div>🪙 Бонусов у клиента: <b style={{ color: '#fbbf24' }}>{Number(bonusInfo.balance).toLocaleString('ru-RU')} ₸</b>
                          {bonusInfo.spent > 0 && <> · списано в этой сделке: <b style={{ color: '#fbbf24' }}>{Number(bonusInfo.spent).toLocaleString('ru-RU')} ₸</b></>}
                        </div>
                        {bonusInfo.canSpend && (
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                            <input inputMode="numeric" placeholder={`до ${Number(bonusInfo.maxSpend).toLocaleString('ru-RU')}`} value={bonusAmt} onChange={e => setBonusAmt(e.target.value.replace(/\D/g, ''))}
                              style={{ width: 120, background: '#1a2030', border: '1px solid #374151', borderRadius: 7, color: '#f9fafb', fontSize: 13, padding: '5px 9px', outline: 'none' }} />
                            <button type="button" onClick={() => setBonusAmt(String(bonusInfo.maxSpend))} style={{ padding: '4px 10px', borderRadius: 8, border: '1px solid #374151', background: 'transparent', color: '#9ca3af', fontSize: 12, cursor: 'pointer' }}>Максимум</button>
                            <button type="button" disabled={bonusBusy || !Number(bonusAmt)} onClick={async () => {
                              setBonusBusy(true);
                              try {
                                await api.post(`/loyalty/deal/${deal.id}/spend`, { amount: Number(bonusAmt) });
                                setBonusAmt('');
                                await reloadProducts();
                                setTimeline(await TimelineAPI.getByDeal(deal.id)); loadPromo(); onUpdated();
                              } catch (e: any) { alert(e?.response?.data?.message ?? 'Не удалось списать бонусы'); }
                              finally { setBonusBusy(false); }
                            }} style={{ padding: '4px 12px', borderRadius: 8, border: '1px solid #fbbf24', background: 'transparent', color: '#fbbf24', fontSize: 12, fontWeight: 600, cursor: 'pointer', opacity: bonusBusy || !Number(bonusAmt) ? 0.5 : 1 }}>
                              {bonusBusy ? '…' : 'Списать бонусы'}
                            </button>
                            <span style={{ color: '#6b7280' }}>макс. {Number(bonusInfo.maxSpend).toLocaleString('ru-RU')} ₸ ({bonusInfo.maxPayPercent}% чека)</span>
                          </div>
                        )}
                        {!bonusInfo.canSpend && bonusInfo.spent === 0 && bonusInfo.reason && <div style={{ color: '#6b7280' }}>{bonusInfo.reason}</div>}
                        {bonusInfo.spent > 0 && payments.length === 0 && (
                          <div><button type="button" disabled={bonusBusy} onClick={async () => {
                            setBonusBusy(true);
                            try {
                              await api.post(`/loyalty/deal/${deal.id}/cancel`);
                              await reloadProducts();
                              setTimeline(await TimelineAPI.getByDeal(deal.id)); loadPromo(); onUpdated();
                            } catch (e: any) { alert(e?.response?.data?.message ?? 'Не удалось отменить'); }
                            finally { setBonusBusy(false); }
                          }} style={{ padding: '3px 10px', borderRadius: 8, border: '1px solid #374151', background: 'transparent', color: '#9ca3af', fontSize: 12, cursor: 'pointer' }}>Отменить списание</button></div>
                        )}
                        {bonusInfo.accrualPercent > 0 && <div style={{ color: '#6b7280' }}>После полной оплаты клиенту начислится {bonusInfo.accrualPercent}% бонусами (≈ {Math.floor(((totalAmount || Number(deal.amount) || 0) - Number(bonusInfo.spent || 0) * 0) * bonusInfo.accrualPercent / 100).toLocaleString('ru-RU')} ₸). Оплата засчитывается, когда платежи добавлены кнопкой «+ Добавить» (поле «Предоплата» не считается).</div>}
                      </div>
                    )}

                    {(promoInfo?.coupons ?? []).length > 0 && (
                      <div style={{ marginBottom: 8, padding: '8px 12px', background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.3)', borderRadius: 8, fontSize: 12, color: '#d1d5db', display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {promoInfo.coupons.map((c: any) => (
                          <div key={c.id}>🎟 {c.kind === 'issued' ? 'Выдан купон' : 'Применён купон'} <b style={{ color: '#a5b4fc', letterSpacing: 1 }}>{c.code}</b>: −{Number(c.percent)}%{c.kind === 'issued' ? `, действует до ${String(c.validUntil).split('-').reverse().join('.')}` : ''}</div>
                        ))}
                      </div>
                    )}

                    {promoInfo && (promoInfo.issues ?? []).filter((i: any) => !i.done).length > 0 && (
                      <div style={{ marginBottom: 8, padding: '8px 12px', background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.3)', borderRadius: 8, fontSize: 12, color: '#d1d5db' }}>
                        {(promoInfo.issues ?? []).filter((i: any) => !i.done).map((i: any) => (
                          <div key={i.promoId}>🎟 «{i.name}»: за эту покупку клиент получит купон −{i.percent}% на {i.days} дн. при оплате{i.needClient ? <b style={{ color: '#fbbf24' }}> — укажите клиента в сделке, иначе купон не выдастся</b> : null}.</div>
                        ))}
                      </div>
                    )}

                    {promoInfo && (promoInfo.badge?.length > 0 || promoInfo.willChange || promoInfo.waiting?.length > 0) && (
                      <div style={{ marginBottom: 8, padding: '8px 12px', background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: 8, fontSize: 12, color: '#d1d5db', display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {(promoInfo.badge ?? []).map((b: any) => (
                          <div key={b.promoId}>🎁 <b style={{ color: '#10b981' }}>{b.name}</b>: {b.isPrize ? `призовой чек №${b.seq}${b.discountSum > 0 ? `, скидка ${Number(b.discountSum).toLocaleString('ru-RU')} ₸` : ''}` : `чек №${b.seq}${b.everyN ? ` из ${b.everyN}` : ''}, скидка не положена`}</div>
                        ))}
                        {promoInfo.willChange && (
                          <div>
                            🎁 Сработает акция «{(promoInfo.applied ?? []).map((a: any) => a.name).join(', ')}»: сумма {Number(promoInfo.currentTotal).toLocaleString('ru-RU')} → <b style={{ color: '#10b981' }}>{Number(promoInfo.newTotal).toLocaleString('ru-RU')} ₸</b>
                            <button disabled={promoBusy} onClick={async () => {
                              setPromoBusy(true);
                              try {
                                await api.post(`/promotions/deal/${deal.id}/apply`);
                                await reloadProducts();
                                const [tl, dealRes] = await Promise.all([TimelineAPI.getByDeal(deal.id), api.get(`/deals/${deal.id}`)]);
                                setTimeline(tl); void dealRes; loadPromo(); onUpdated();
                              } catch (e: any) { alert(e?.response?.data?.message ?? 'Не удалось применить акцию'); }
                              finally { setPromoBusy(false); }
                            }} style={{ marginLeft: 8, padding: '3px 10px', borderRadius: 8, border: '1px solid #10b981', background: 'transparent', color: '#10b981', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                              {promoBusy ? '…' : 'Применить сейчас'}
                            </button>
                          </div>
                        )}
                        {!promoInfo.locked && !promoInfo.willChange && (promoInfo.waiting ?? []).map((w: any) => (
                          <div key={w.promoId} style={{ color: '#9ca3af' }}>🎁 «{w.name}»: этот чек получит номер {w.seq}, скидка не положена (призовой каждый N-й). Номер закрепится при первой оплате.</div>
                        ))}
                      </div>
                    )}

                    {(receiptUrl || payments.length > 0 || deal.status === "won" || Number(prepayment) > 0) && (
                      <div style={{ marginBottom: 8, padding: '8px 12px', background: receiptUrl ? 'rgba(16,185,129,0.08)' : 'rgba(201,164,92,0.08)', border: `1px solid ${receiptUrl ? 'rgba(16,185,129,0.25)' : 'rgba(201,164,92,0.3)'}`, borderRadius: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 16 }}>🧾</span>
                        <span style={{ fontSize: 12, color: '#6b7280' }}>{receiptUrl ? "Фискальный чек:" : "Чек (не фискальный):"}</span>
                        {receiptUrl ? (
                          <a href={receiptUrl} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: '#10b981', textDecoration: 'underline' }}>Открыть чек</a>
                        ) : (
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                const { data } = await api.get(`/receipts/deal/${deal.id}/link`);
                                window.open(data.url, "_blank", "noopener");
                              } catch { alert("Чек доступен для сделок с оплатой"); }
                            }}
                            style={{ background: "none", border: "none", padding: 0, fontSize: 12, color: '#c9a45c', textDecoration: 'underline', cursor: 'pointer' }}
                          >
                            Открыть чек
                          </button>
                        )}
                        <button
                          type="button"
                          title="Красивый чек с QR для проверки в ОФД — эту ссылку можно отправить клиенту"
                          onClick={async () => {
                            try {
                              const { data } = await api.get(`/receipts/deal/${deal.id}/link`);
                              try { await navigator.clipboard.writeText(data.url); alert("Ссылка на чек для клиента скопирована:\n" + data.url); }
                              catch { window.prompt("Ссылка на чек для клиента:", data.url); }
                            } catch { alert("Не удалось получить ссылку на чек"); }
                          }}
                          style={{ marginLeft: "auto", background: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.35)', color: '#a5b4fc', borderRadius: 8, padding: '4px 10px', fontSize: 12, cursor: 'pointer' }}
                        >
                          📋 Ссылка для клиента
                        </button>
                      </div>
                    )}

                    {payFormOpen && (() => {
                      const alreadyPaid = payments.reduce((s: number, p: any) => s + Number(p.amount), 0);
                      const dealTotal = totalAmount || Number(deal.amount) || 0;
                      const remaining = Math.round((dealTotal - alreadyPaid) * 100) / 100;
                      const linesTotal = Math.round(payLines.reduce((s, l) => s + (Number(l.amount) || 0), 0) * 100) / 100;
                      const isExact = Math.abs(linesTotal - remaining) < 0.01;
                      const isOver = linesTotal > remaining + 0.01;
                      const canSave = !payAdding && isExact && payLines.every(l => l.amount && l.method);
                      const availMethods = paymentMethods.length > 0 ? paymentMethods : Object.keys(ALL_PAY_LABELS);

                      return (
                        <div style={{ padding: '10px 12px', background: '#0d1117', border: '1px solid #1f2937', borderRadius: 10, display: 'flex', flexDirection: 'column', gap: 10 }}>
                          {alreadyPaid > 0 && (
                            <div style={{ fontSize: 12, color: '#6b7280' }}>Оплачено ранее: <b style={{ color: '#d1d5db' }}>{alreadyPaid.toLocaleString('ru-RU')} ₸</b></div>
                          )}
                          <div style={{ fontSize: 12, color: '#6b7280' }}>
                            Итого к оплате: <b style={{ color: '#10b981' }}>{remaining.toLocaleString('ru-RU')} ₸</b>
                          </div>

                          {/* Строки оплаты */}
                          {payLines.map((line, idx) => (
                            <div key={idx} style={{ background: '#111827', borderRadius: 8, padding: '10px 10px 8px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                              <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                                {availMethods.map(m => (
                                  <button key={m} onClick={() => setPayLines(ls => ls.map((l, i) => i === idx ? { ...l, method: m } : l))}
                                    style={{ padding: '3px 9px', borderRadius: 12, fontSize: 11, cursor: 'pointer', fontWeight: 600,
                                      border: `1px solid ${line.method === m ? '#10b981' : '#374151'}`,
                                      background: line.method === m ? 'rgba(16,185,129,0.12)' : 'transparent',
                                      color: line.method === m ? '#10b981' : '#6b7280' }}>
                                    {ALL_PAY_LABELS[m] ?? m}
                                  </button>
                                ))}
                              </div>
                              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                <input type="number" placeholder="Сумма" value={line.amount}
                                  onChange={e => setPayLines(ls => ls.map((l, i) => i === idx ? { ...l, amount: e.target.value } : l))}
                                  style={{ flex: 1, background: '#1a2030', border: '1px solid #374151', borderRadius: 7, color: '#f9fafb', fontSize: 13, padding: '7px 10px', outline: 'none' }} />
                                {payLines.length > 1 && (
                                  <button onClick={() => setPayLines(ls => ls.filter((_, i) => i !== idx))}
                                    style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: 16, padding: '0 4px' }}>×</button>
                                )}
                              </div>
                            </div>
                          ))}

                          <button onClick={() => setPayLines(ls => [...ls, { method: availMethods[0] ?? 'cash', amount: '' }])}
                            style={{ fontSize: 12, color: '#6b7280', background: 'none', border: '1px dashed #374151', borderRadius: 7, padding: '5px', cursor: 'pointer' }}>
                            + добавить способ оплаты
                          </button>

                          {/* Итог строк */}
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, borderTop: '1px solid #1f2937', paddingTop: 6 }}>
                            <span style={{ color: '#6b7280' }}>Сумма к проведению:</span>
                            <b style={{ color: isOver ? '#f87171' : isExact ? '#10b981' : '#fbbf24' }}>{linesTotal.toLocaleString('ru-RU')} ₸</b>
                          </div>
                          {isOver && <div style={{ fontSize: 12, color: '#f87171' }}>⛔ Превышает остаток на {(linesTotal - remaining).toLocaleString('ru-RU')} ₸</div>}
                          {!isExact && !isOver && linesTotal > 0 && <div style={{ fontSize: 11, color: '#fbbf24' }}>⚠️ Не хватает {(remaining - linesTotal).toLocaleString('ru-RU')} ₸ — сумма должна равняться остатку</div>}

                          {myRole === "TECHNICIAN" && payLines.some((l) => l.method === "cash") && (
                            <div style={{ fontSize: 12, color: "#fbbf24", background: "rgba(251,191,36,0.08)", border: "1px solid rgba(251,191,36,0.3)", borderRadius: 8, padding: "7px 10px" }}>
                              💵 Наличные запишутся на вас как долг — сдайте их в кассу, руководитель или бухгалтер отметит это в разделе «Касса / Долги».
                            </div>
                          )}
                          <input placeholder="Примечание (необязательно)" value={payNote} onChange={e => setPayNote(e.target.value)}
                            style={{ background: '#1a2030', border: '1px solid #374151', borderRadius: 7, color: '#f9fafb', fontSize: 13, padding: '7px 10px', outline: 'none', width: '100%', boxSizing: 'border-box' as const }} />

                          <button disabled={!canSave}
                            onClick={async () => {
                              if (!canSave) return;
                              setPayAdding(true);
                              try {
                                for (const line of payLines) {
                                  await api.post(`/deals/${deal.id}/payments`, { amount: Number(line.amount), method: line.method, note: payNote || null, createdBy: getCurrentUserName() });
                                }
                                const [res, tl, dealRes] = await Promise.all([api.get(`/deals/${deal.id}/payments`), TimelineAPI.getByDeal(deal.id), api.get(`/deals/${deal.id}`)]);
                                setPayments(res.data);
                                setTimeline(tl);
                                if (dealRes.data?.receiptUrl) setReceiptUrl(dealRes.data.receiptUrl);
                                setPayLines([{ method: availMethods[0] ?? 'cash', amount: '' }]);
                                setPayNote('');
                                setPayFormOpen(false);
                              } catch (e: any) {
                                const data = e?.response?.data;
                                if (e?.response?.status === 409 && data?.promoApplied) {
                                  // сработала акция: сумма сделки изменилась, оплату нужно ввести заново
                                  alert(data.message);
                                  await reloadProducts();
                                  const [res, tl] = await Promise.all([api.get(`/deals/${deal.id}/payments`), TimelineAPI.getByDeal(deal.id)]);
                                  setPayments(res.data); setTimeline(tl); loadPromo(); onUpdated();
                                  setPayLines([{ method: availMethods[0] ?? 'cash', amount: '' }]);
                                } else {
                                  alert(data?.message ?? 'Не удалось провести оплату');
                                }
                              } finally { setPayAdding(false); }
                            }}
                            style={{ padding: '8px', borderRadius: 8, background: canSave ? '#10b981' : '#1f2937', color: canSave ? '#fff' : '#4b5563', border: 'none', fontWeight: 700, cursor: canSave ? 'pointer' : 'not-allowed', fontSize: 13 }}>
                            {payAdding ? 'Сохраняем...' : '✓ Провести оплату'}
                          </button>
                        </div>
                      );
                    })()}
                  </div>

                  {/* Метод оплаты — редактируемый */}
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase" as const, letterSpacing: "0.06em", color: "#6b7280", marginBottom: 6 }}>Метод оплаты</div>
                    <div className="pay-options-grid" style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {PAY_LIST.filter(p => (paymentMethods.length === 0 || paymentMethods.includes(p.value)) && (p.value !== 'credit' || creditAvailable)).map((p) => (
                        <button key={p.value} onClick={() => setPaymentMethod(p.value)}
                          style={{
                            flex: 1, padding: "7px 0", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer",
                            border: `1px solid ${paymentMethod === p.value ? p.color : "#374151"}`,
                            background: paymentMethod === p.value ? `${p.color}18` : "transparent",
                            color: paymentMethod === p.value ? p.color : "#6b7280",
                            transition: "all 0.15s", minWidth: 70,
                          }}>
                          {p.icon} {p.label}
                        </button>
                      ))}
                    </div>

                    {/* Кредитный блок */}
                    {paymentMethod === "credit" && (
                      <div style={{ marginTop: 10, padding: "12px 14px", background: "rgba(167,139,250,0.07)", border: "1px solid rgba(167,139,250,0.25)", borderRadius: 10 }}>
                        {!clientId ? (
                          <div style={{ fontSize: 13, color: "#a78bfa" }}>⚠️ Выберите клиента для проверки кредитного лимита</div>
                        ) : creditLoading ? (
                          <div style={{ fontSize: 13, color: "#6b7280" }}>Проверяем кредитный лимит...</div>
                        ) : !creditBalance?.account ? (
                          <div style={{ fontSize: 13, color: "#f87171" }}>❌ У клиента нет кредитного аккаунта</div>
                        ) : (() => {
                          const acc = creditBalance.account;
                          const available = Number(creditBalance.availableAmount);
                          const totalAmount = Math.max(0, products.reduce((s: number, p: any) => s + p.quantity * p.price, 0) - (disc?.discount ?? 0));
                          const hasOverdue = creditBalance.overdueOrders?.length > 0;
                          const enoughBalance = available >= totalAmount;
                          const canCredit = !hasOverdue && enoughBalance && acc.isActive;
                          return (
                            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                              <div style={{ display: "flex", gap: 16, fontSize: 12, flexWrap: "wrap" }}>
                                <span style={{ color: "#6b7280" }}>Лимит: <strong style={{ color: "#a5b4fc" }}>{Number(acc.limitAmount).toLocaleString("ru")} ₸</strong></span>
                                <span style={{ color: "#6b7280" }}>Доступно: <strong style={{ color: enoughBalance ? "#10b981" : "#ef4444" }}>{available.toLocaleString("ru")} ₸</strong></span>
                                <span style={{ color: "#6b7280" }}>Срок: <strong style={{ color: "#f9fafb" }}>{acc.paymentTermDays} дн.</strong></span>
                              </div>
                              {hasOverdue && <div style={{ fontSize: 12, color: "#f87171" }}>⚠️ Есть просроченные платежи — новый кредит невозможен</div>}
                              {!enoughBalance && !hasOverdue && <div style={{ fontSize: 12, color: "#f87171" }}>⚠️ Недостаточно лимита: нужно {totalAmount.toLocaleString("ru")} ₸, доступно {available.toLocaleString("ru")} ₸</div>}
                              {canCredit && (
                                <div style={{ fontSize: 12, color: "#10b981" }}>
                                  ✓ Нажмите «Сохранить» — кредитный заказ создастся автоматически
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                </section>
                )}

                {tab === "items" && (
                <section style={{ ...cardSt, order: 2 }}>
                  <SectionTitle>🔑 Ключи активации</SectionTitle>
                  <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 8 }}>
                    Вводите по одному ключу на строку. После сохранения — клиент увидит их в личном кабинете.
                  </div>
                  <textarea
                    rows={4}
                    placeholder={"XXXXX-XXXXX-XXXXX-XXXXX\nXXXXX-XXXXX-XXXXX-XXXXX"}
                    value={keysText}
                    onChange={e => setKeysText(e.target.value)}
                    style={{ ...inputSt, width: "100%", resize: "vertical", fontFamily: "monospace", fontSize: 13, boxSizing: "border-box", minHeight: 80 }}
                  />
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 8 }}>
                    <button
                      disabled={keysSaving}
                      onClick={async () => {
                        setKeysSaving(true);
                        try {
                          const keys = keysText.split("\n").map(k => k.trim()).filter(Boolean);
                          await DealsAPI.updateKeys(deal.id, keys);
                          await TimelineAPI.add({ dealId: deal.id, type: "system", message: `Ключи активации обновлены (${keys.length} шт.)` });
                          const tl = await TimelineAPI.getByDeal(deal.id);
                          setTimeline(tl);
                        } finally {
                          setKeysSaving(false);
                        }
                      }}
                      style={{ ...priBtn, padding: "6px 16px", fontSize: 12 }}>
                      {keysSaving ? "Сохраняем…" : "💾 Сохранить ключи"}
                    </button>
                    {keysText.split("\n").filter(k => k.trim()).length > 0 && (
                      <span style={{ fontSize: 12, color: "#10b981" }}>
                        {keysText.split("\n").filter(k => k.trim()).length} шт.
                      </span>
                    )}
                    {(deal.activationKeys ?? []).length > 0 && (
                      <span style={{ fontSize: 11, color: "#6b7280" }}>
                        Сохранено: {(deal.activationKeys ?? []).length} шт.
                      </span>
                    )}
                  </div>
                </section>
                )}

                {tab === "items" && (
                <section style={{ ...cardSt, order: 1 }}>
                  <SectionTitle>Товарные позиции</SectionTitle>
                  <ProductSelectV2 products={allProducts} onSelect={(p) => addProduct(p.id)} />

                  {products.length > 0 && (
                    <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 6 }}>
                      {products.map((p: any) => (
                        <Fragment key={p.id}>
                        <div className="deal-prod-row" style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 11px", borderRadius: 8, background: "#131926", border: "1px solid #1f2937" }}>
                          <div className="deal-prod-name" style={{ flex: 1, fontSize: 13, color: "#e5e7eb", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
                            {p.product?.nameRu ?? `Товар #${p.productId}`}
                          </div>
                          <input type="number" value={p.quantity} min={1}
                            onChange={(e) => updateQty(p.id, Number(e.target.value))}
                            style={{ ...inputSt, width: 50, textAlign: "center", padding: "4px 6px", flexShrink: 0 }} />
                          {(() => {
                            const st = allProducts.find((x) => x.id === p.productId)?.stock;
                            if (st === null || st === undefined) return <span style={{ width: 62, flexShrink: 0 }} />;
                            const low = st < p.quantity;
                            return (
                              <span
                                title={low ? `Не хватает ${p.quantity - st} шт` : "Остаток на складе"}
                                style={{
                                  width: 62, flexShrink: 0, textAlign: "center", fontSize: 11, fontWeight: 600, borderRadius: 6, padding: "3px 0",
                                  color: low ? "#f87171" : "#34d399",
                                  background: low ? "rgba(239,68,68,0.12)" : "rgba(16,185,129,0.1)",
                                }}
                              >
                                ост. {st}
                              </span>
                            );
                          })()}
                          <span style={{ color: "#6b7280", fontSize: 12, flexShrink: 0 }}>×</span>
                          <input type="number" value={p.price}
                            onChange={(e) => updatePriceLocal(p.id, Number(e.target.value))}
                            onBlur={() => updatePriceServer(p.id)}
                            style={{ ...inputSt, width: 80, textAlign: "right", padding: "4px 8px", flexShrink: 0 }} />
                          <span style={{ color: "#6b7280", fontSize: 12, flexShrink: 0 }}>₸</span>
                          <span style={{ color: "#c084fc", fontWeight: 700, fontSize: 13, width: 76, textAlign: "right", flexShrink: 0 }}>
                            {fmtMoney(p.price * p.quantity)}
                          </span>
                          <button onClick={() => removeProduct(p.id)} style={{ background: "none", border: "none", color: "#4b5563", cursor: "pointer", fontSize: 16, lineHeight: 1, padding: 0, flexShrink: 0 }}>×</button>
                        </div>
                        {p.product?.requireSerial && (() => {
                          const cur = serials[p.productId] ?? [];
                          const arr = Array.from({ length: Number(p.quantity) || 0 }, (_, i) => cur[i] ?? "");
                          const filled = arr.filter((s) => s.trim()).length;
                          return (
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, padding: "0 11px 6px" }}>
                              <div style={{ flex: "0 0 100%", fontSize: 11.5, color: filled === arr.length ? "#34d399" : "#fbbf24" }}>
                                Серийные номера: {filled} из {arr.length}. Без них сделку нельзя завершить.
                              </div>
                              {arr.map((v, i) => (
                                <input
                                  key={i}
                                  data-dsn={p.productId}
                                  data-i={i}
                                  placeholder={`S/N ${i + 1}`}
                                  value={v}
                                  onChange={(ev) => setSerialLocal(p.productId, i, ev.target.value, arr.length)}
                                  onBlur={() => saveSerials(p.productId)}
                                  onKeyDown={(ev) => { if (ev.key === "Enter") { ev.preventDefault(); (ev.target as HTMLInputElement).blur(); setTimeout(focusNextSn, 120); } }}
                                  style={{ ...inputSt, flex: "1 1 160px", minWidth: 0 }}
                                />
                              ))}
                            </div>
                          );
                        })()}
                        </Fragment>
                      ))}
                      <DiscountBox
                        dealId={deal.id}
                        state={disc ? { ...disc, productsSum } : null}
                        isAdmin={myRole === "ADMIN"}
                        meId={meId}
                        canEdit={deal.status === "active" && !readOnly}
                        onChanged={discChanged}
                      />
                      <div style={{ display: "flex", justifyContent: "flex-end", padding: "7px 11px", fontWeight: 700, color: "#c084fc", fontSize: 14, borderTop: "1px solid #1f2937" }}>
                        Итого: {fmtMoney(totalAmount)}
                      </div>
                    </div>
                  )}
                </section>
                )}
              </div>
            </>
          )}

          {/* ── HISTORY TAB ── */}
          {tab === "history" && (
            <>
              <div className="edit-deal-right" style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
                <div style={{ padding: "14px 16px 8px", borderBottom: "1px solid #1f2937", flexShrink: 0 }}>
                  <SectionTitle>История</SectionTitle>
                </div>

                <div style={{ flex: 1, overflowY: "auto", padding: "12px 16px", display: "flex", flexDirection: "column", gap: 10 }}>
                  {timeline.length === 0 && (
                    <div style={{ color: "#4b5563", fontSize: 13, textAlign: "center", padding: "30px 0" }}>Нет записей</div>
                  )}
                  {timeline.map((e: any) => (
                    <div key={e.id} style={{ display: "flex", gap: 8 }}>
                      <div style={{ fontSize: 15, flexShrink: 0, marginTop: 1 }}>{tlIcon[e.type] ?? "📌"}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, color: "#d1d5db", lineHeight: 1.45, wordBreak: "break-word" }}>{e.message}</div>
                        <div style={{ fontSize: 11, color: "#4b5563", marginTop: 3 }}>{fmtDate(e.createdAt)}</div>
                      </div>
                    </div>
                  ))}
                  <div ref={timelineEndRef} />
                </div>

                <div style={{ padding: "10px 16px", borderTop: "1px solid #1f2937", flexShrink: 0 }}>
                  <textarea rows={2} placeholder="Добавить комментарий..."
                    value={comment} onChange={(e) => setComment(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendComment(); } }}
                    style={{ ...inputSt, width: "100%", resize: "none", boxSizing: "border-box", marginBottom: 7 }} />
                  <button onClick={sendComment} disabled={sending || !comment.trim()}
                    style={{
                      width: "100%", padding: "7px 0", borderRadius: 8, fontSize: 13, fontWeight: 600,
                      cursor: comment.trim() ? "pointer" : "not-allowed", border: "none",
                      background: comment.trim() ? "#6366f1" : "#1f2937",
                      color: comment.trim() ? "#fff" : "#4b5563", transition: "all 0.15s",
                    }}>
                    {sending ? "Отправка…" : "Отправить"}
                  </button>
                </div>
              </div>
            </>
          )}

          {/* ── CALLS TAB ── */}
          {tab === "calls" && (
            <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>
              <SectionTitle>Звонки по сделке</SectionTitle>
              {dealCalls.length === 0 ? (
                <div style={{ color: "#4b5563", fontSize: 13, padding: "24px 0" }}>
                  Звонков пока нет. Входящие и исходящие звонки клиента попадают сюда автоматически в течение минуты после разговора.
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10 }}>
                  {dealCalls.map((c: any) => {
                    const missed = c.status !== "answered";
                    const dur = (n: number) => (n >= 60 ? `${Math.floor(n / 60)} мин ${String(n % 60).padStart(2, "0")} с` : `${n} с`);
                    const title = c.direction === "outbound"
                      ? (missed ? `📤 Исходящий на ${c.remoteFormatted}: не дозвонились` : `📤 Исходящий на ${c.remoteFormatted}`)
                      : (missed ? `📵 Пропущенный от ${c.remoteFormatted}` : `📞 Входящий от ${c.remoteFormatted}`);
                    const sub = [c.status === "answered" ? `разговор ${dur(c.talkSeconds || 0)}` : (c.ringSeconds ? `звонил ${dur(c.ringSeconds)}` : ""), c.answeredBy ? (c.direction === "outbound" ? `звонил: ${c.answeredBy}` : `ответил: ${c.answeredBy}`) : ""].filter(Boolean).join(" · ");
                    return (
                      <div key={c.id} style={{ padding: "10px 12px", borderRadius: 10, background: "#131926", borderLeft: `3px solid ${missed ? "#ef4444" : "#10b981"}` }}>
                        <div style={{ fontSize: 13, color: "#e5e7eb", fontWeight: 600 }}>{title}</div>
                        {sub && <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 2 }}>{sub}</div>}
                        <div style={{ fontSize: 11, color: "#6b7280", marginTop: 3 }}>{fmtDate(c.startedAt)}</div>
                        {c.hasRecording && (
                          <div style={{ marginTop: 8 }}>
                            {audio[c.id] ? (
                              <audio controls autoPlay src={audio[c.id]} style={{ width: "100%", maxWidth: 420, height: 36 }} />
                            ) : (
                              <button
                                disabled={audioBusy === c.id}
                                onClick={async () => {
                                  setAudioBusy(c.id);
                                  try {
                                    const r = await api.get(`/telephony/recording/${c.id}`, { responseType: "blob" });
                                    setAudio((a) => ({ ...a, [c.id]: URL.createObjectURL(r.data as Blob) }));
                                  } catch (e: any) {
                                    alert(e?.response?.status === 404 ? "Файл записи не найден на сервере" : "Не удалось загрузить запись");
                                  } finally { setAudioBusy(null); }
                                }}
                                style={{ padding: "5px 12px", borderRadius: 8, border: "1px solid #374151", background: "#1f2937", color: "#e5e7eb", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
                                {audioBusy === c.id ? "Загрузка…" : "▶ Прослушать запись"}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── DOCUMENTS TAB ── */}
          {tab === "docs" && (
            <div style={{ flex: 1, overflowY: "auto", padding: "18px 20px" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 18 }}>
                <DocGroup title="Стандартные документы" color="#818cf8">
                  {(["offer", "invoice", "avr", "waybill", "warranty"] as DocumentType[]).map((type) => (
                    <DocAdd key={type} color="#818cf8" onClick={() => createDocument(type)}>{DOC_LABELS[type]}</DocAdd>
                  ))}
                </DocGroup>
                <DocGroup title="Видеонаблюдение" hint="смета, договор и акт связаны: данные берутся из сделки" color="#10b981">
                  <DocAdd color="#10b981" onClick={() => setShowContractModal('estimate')}>Смета</DocAdd>
                  <DocAdd color="#10b981" onClick={() => setShowContractModal('contract')}>Договор</DocAdd>
                  <DocAdd color="#10b981" onClick={() => setShowContractModal('act')}>Акт</DocAdd>
                </DocGroup>
                <DocGroup title="Ремонт и сервис" color="#f59e0b">
                  <DocAdd color="#f59e0b" onClick={() => setShowWorkOrder(true)}>Заказ-наряд</DocAdd>
                  <DocAdd color="#f59e0b" onClick={() => createDocument("avr_pc")}>АВР физлицо (ПК)</DocAdd>
                  <DocAdd color="#f59e0b" onClick={() => createDocument("intake")}>Приём техники</DocAdd>
                </DocGroup>
              </div>

              {showIntake && (
                <IntakePanel
                  initial={documents.find((d) => d.type === "intake")?.meta?.intake ?? null}
                  onCancel={() => setShowIntake(false)}
                  onSubmit={async (data: IntakeData) => {
                    await doGenerate("intake", { intake: data });
                    setShowIntake(false);
                  }}
                />
              )}

              {dealWorkOrders.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.4 }}>Заказ-наряды по сделке</div>
                  {dealWorkOrders.map((o) => (
                    <div key={o.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 12px", borderRadius: 10, background: "#131926", border: "1px solid #1f2937", marginBottom: 6 }}>
                      <span style={{ fontSize: 16 }}>🛠</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "#e5e7eb" }}>{o.number} · {o.title}</div>
                        <div style={{ fontSize: 11, color: "#6b7280" }}>
                          <span style={{ color: STATUS_COLORS[o.status], fontWeight: 600 }}>{STATUS_LABELS[o.status]}</span>
                          {" · "}{o.assigneeName ?? "исполнитель не назначен"}
                        </div>
                      </div>
                      {(o.status === "new" || o.status === "in_progress") && (
                        <SmallBtn onClick={async () => {
                          try {
                            const r = await WorkOrdersAPI.syncFromDeal(o.id);
                            alert(r.added.length || r.changed.length
                              ? `Наряд обновлён.${r.added.length ? `\nДобавлено: ${r.added.join("; ")}` : ""}${r.changed.length ? `\nКоличество: ${r.changed.join("; ")}` : ""}`
                              : "Наряд уже совпадает со сделкой");
                            loadDealWorkOrders();
                          } catch (e: any) {
                            alert(e?.response?.data?.message ?? "Не удалось обновить наряд");
                          }
                        }}>↻ Из сделки</SmallBtn>
                      )}
                      <SmallBtn onClick={() => WorkOrdersAPI.openPdf(o.id)}>PDF</SmallBtn>
                    </div>
                  ))}
                </div>
              )}

              {docParamsType && (
                <div style={{ padding: 14, borderRadius: 10, background: "#131926", border: "1px solid #1f2937", marginBottom: 14 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10, color: "#e5e7eb" }}>Параметры — {DOC_LABELS[docParamsType]}</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 10 }}>
                    {[
                      { key: "docNumber", label: "Номер", placeholder: "АВР-105/2026" },
                      { key: "contract", label: "Договор", placeholder: "Название или номер" },
                      { key: "docDate", label: "Дата", placeholder: "дд.мм.гггг" },
                    ].map(({ key, label, placeholder }) => (
                      <label key={key} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        <span style={{ fontSize: 11, color: "#6b7280" }}>{label}</span>
                        <input style={inputSt} placeholder={placeholder}
                          value={(docParamsForm as any)[key]}
                          onChange={(e) => setDocParamsForm((f) => ({ ...f, [key]: e.target.value }))} />
                      </label>
                    ))}
                  </div>
                  <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                    <button style={secBtn} onClick={() => setDocParamsType(null)}>Отмена</button>
                    <button style={priBtn} onClick={async () => {
                      const t = docParamsType; setDocParamsType(null);
                      await doGenerate(t, docParamsForm);
                    }}>Создать</button>
                  </div>
                </div>
              )}

              <p style={{ fontSize: 12, color: "#4b5563", marginBottom: 14 }}>
                АВР — для услуг (ст. 176 НК РК) | Накладная — для товаров (ст. 412 НК РК)
              </p>

              {documents.length === 0 ? (
                <div style={{ color: "#4b5563", textAlign: "center", padding: "40px 0", fontSize: 14 }}>Документы ещё не созданы</div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {documents.map((doc) => {
                    const label = DOC_LABELS[doc.type as DocumentType] ?? doc.type;
                    const isVideo = VIDEO_DOC_TYPES.includes(doc.type as DocumentType);
                    const noExcel = NO_EXCEL_TYPES.includes(doc.type as DocumentType);
                    const meta = doc.meta ?? {};
                    const fmt = (iso: string) => new Date(iso).toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
                    return (
                      <div key={doc.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 13px", borderRadius: 10, background: "#131926", border: "1px solid #1f2937" }}>
                        <span style={{ fontSize: 16 }}>📄</span>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: "#e5e7eb" }}>{label}</div>
                          <div style={{ fontSize: 11, color: "#6b7280" }}>
                            {meta.docDate ? `от ${new Date(meta.docDate + "T00:00:00").toLocaleDateString("ru-RU")}` : new Date(doc.createdAt).toLocaleDateString("ru-RU")}
                          </div>
                          {doc.type === "contract" && (
                            <div style={{ fontSize: 11, marginTop: 2, color: meta.signedAt ? "#10b981" : meta.viewedAt ? "#f59e0b" : "#6b7280" }}>
                              {meta.signedAt
                                ? `✓ Подписан клиентом в ЛК ${fmt(meta.signedAt)}`
                                : meta.viewedAt ? `Клиент открыл в ЛК ${fmt(meta.viewedAt)}, не подписан` : "Клиент ещё не открывал в ЛК"}
                            </div>
                          )}
                        </div>
                        <div style={{ display: "flex", gap: 6 }}>
                          <SmallBtn title="Скачать PDF" onClick={() => DocumentsAPI.downloadPdf(doc.id)}>PDF</SmallBtn>
                          {isDesktop && doc.type === "intake_label" && (
                            <select title="Размер этикетки, мм (рулон в принтере)" defaultValue={labelSizeKey(getLabelSize())}
                              onChange={(e) => setLabelSize(e.target.value)}
                              style={{ minHeight: 28, borderRadius: 6, fontSize: 12, background: "#1a2030", color: "#e5e7eb", border: "1px solid #374151", padding: "0 4px" }}>
                              {LABEL_SIZES.map((s) => <option key={labelSizeKey(s)} value={labelSizeKey(s)}>{s.w}×{s.h}</option>)}
                            </select>
                          )}
                          {isDesktop && <SmallBtn title="Печать" onClick={() => printDocument(doc, true)}>🖨</SmallBtn>}
                          {!noExcel && <SmallBtn title="Скачать Excel" onClick={() => DocumentsAPI.downloadExcel(doc.id)}>Excel</SmallBtn>}
                          <SmallBtn title="Пересоздать документ" onClick={async () => {
                            if (doc.type === "intake_label") { await doGenerate("intake_label"); return; }
                            if (!isVideo) return createDocument(doc.type as DocumentType);
                            if (meta.signedAt && !window.confirm("Договор уже подписан клиентом. Пересоздание сбросит подпись — продолжить?")) return;
                            setShowContractModal(doc.type as "estimate" | "contract" | "act");
                          }}>↺</SmallBtn>
                          <SmallBtn danger title="Удалить документ" onClick={async () => {
                            const warn = meta.signedAt ? `Документ «${label}» уже подписан клиентом. Удалить вместе с подписью?` : `Удалить "${label}"?`;
                            if (!window.confirm(warn)) return;
                            await DocumentsAPI.delete(doc.id);
                            await refreshAll();
                          }}>✕</SmallBtn>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Return to work section ── */}
        {showReturnToWork && deal.status !== 'active' && (
          <div style={{ padding: '14px 20px', borderTop: '2px solid rgba(99,102,241,0.3)', background: 'rgba(99,102,241,0.05)', flexShrink: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#818cf8', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>
              Вернуть в работу
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: 11, color: '#6b7280' }}>Воронка</span>
                <select
                  value={returnPipelineId ?? ''}
                  onChange={async (e) => {
                    const pid = Number(e.target.value);
                    setReturnPipelineId(pid);
                    const stageList = await getStages(pid);
                    setReturnStages(stageList);
                    setReturnStageId(stageList[0]?.id ?? null);
                  }}
                  style={{ ...inputSt, padding: '7px 10px', fontSize: 12, minWidth: 160 }}
                >
                  {pipelines.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: 11, color: '#6b7280' }}>Стадия</span>
                <select
                  value={returnStageId ?? ''}
                  onChange={e => setReturnStageId(Number(e.target.value))}
                  style={{ ...inputSt, padding: '7px 10px', fontSize: 12, minWidth: 160 }}
                >
                  {returnStages.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <button
                disabled={returnSaving || !returnPipelineId || !returnStageId}
                onClick={async () => {
                  if (!returnPipelineId || !returnStageId) return;
                  setReturnSaving(true);
                  try {
                    await DealsAPI.update(deal.id, { status: 'active' });
                    await DealsAPI.updateStage(deal.id, { pipelineId: returnPipelineId, stageId: returnStageId });
                    await onUpdated();
                    onClose();
                  } finally {
                    setReturnSaving(false);
                  }
                }}
                style={{ ...priBtn, background: returnSaving ? '#374151' : '#6366f1', padding: '8px 20px', whiteSpace: 'nowrap' as const }}
              >
                {returnSaving ? 'Возвращаем...' : '↩ Вернуть в работу'}
              </button>
            </div>
          </div>
        )}

        {/* ── Footer ── */}
        <div className="edit-deal-footer" style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 10, padding: "12px 20px", borderTop: "1px solid #1f2937", flexShrink: 0, flexWrap: "wrap" }}>
          <div style={{ marginRight: "auto", fontSize: 13, color: "#9ca3af" }}>
            Итого: <b style={{ color: "#c084fc", fontSize: 16 }}>{fmtMoney(totalAmount || Number(deal.amount))}</b>
          </div>
          <button style={secBtn} onClick={onClose}>{readOnly ? "Закрыть" : "Отмена"}</button>
          {deal.status === "active" && !readOnly && (
            <button
              style={{ ...secBtn, color: "#f59e0b", borderColor: "#f59e0b44" }}
              onClick={async () => {
                if (!window.confirm("Завершить сделку без фискального чека?")) return;
                try {
                  await save();
                  await DealsAPI.winNoFiscal(deal.id);
                  onUpdated();
                  onClose();
                } catch (e: any) {
                  const d = e?.response?.data;
                  // нехватку на складе показывает отдельное окно (api/axios.ts), остальные причины — сообщением
                  if (d?.code !== "INSUFFICIENT_STOCK") alert(typeof d?.message === "string" ? d.message : "Не удалось завершить сделку");
                }
              }}
            >
              ✓ Без чека
            </button>
          )}
          {perms.canRequestDeleteDeals && !readOnly && !delReq && deal.status === "active" && (
            <button
              style={{ ...secBtn, color: "#f87171", borderColor: "#f8717144" }}
              onClick={async () => {
                const reason = window.prompt("Причина удаления (необязательно). Запрос уйдёт руководителю:", "");
                if (reason === null) return;
                try {
                  await DealsAPI.requestDelete(deal.id, reason);
                  setDelReq({ at: new Date().toISOString(), by: null, reason: reason || null });
                  setTimeline(await TimelineAPI.getByDeal(deal.id));
                  onUpdated();
                } catch (e: any) {
                  alert(e?.response?.data?.message ?? "Не удалось отправить запрос");
                }
              }}
            >
              🗑 Запросить удаление
            </button>
          )}
          {!readOnly && <button style={priBtn} onClick={save}>Сохранить</button>}
        </div>

        {showCreateClient && (
          <CreateClientModal prefill={createClientPrefill}
            onClose={() => setShowCreateClient(false)}
            onCreated={(c) => { if (!clients.some((x: any) => x.id === c.id)) clients.push(c); setClientId(c.id); setShowCreateClient(false); }} />
        )}

        {showWorkOrder && (
          <CreateWorkOrderModal
            dealId={deal.id}
            onClose={() => setShowWorkOrder(false)}
            onSaved={() => { setShowWorkOrder(false); loadDealWorkOrders(); }}
          />
        )}

        {showContractModal && (
          <CreateContractModal
            dealId={deal.id}
            documentType={showContractModal}
            onClose={() => setShowContractModal(null)}
            onSuccess={async () => {
              setShowContractModal(null);
              await refreshAll();
            }}
          />
        )}
      </div>
    </div>
  );
};

const cardSt: React.CSSProperties = {
  background: "#11151f", border: "1px solid #1f2937", borderRadius: 12, padding: "14px 16px",
};

// ── Мелкие компоненты ─────────────────────────────────────────

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>
      {children}
    </div>
  );
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick} style={{
      padding: "5px 13px", borderRadius: 7, fontSize: 13, fontWeight: active ? 600 : 400,
      cursor: "pointer", border: `1px solid ${active ? "#6366f1" : "#1f2937"}`,
      background: active ? "#6366f1" : "transparent", color: active ? "#fff" : "#9ca3af",
      transition: "all 0.15s", flexShrink: 0,
    }}>
      {children}
    </button>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      <label style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</label>
      {children}
    </div>
  );
}

function SmallBtn({ onClick, danger, title, children }: { onClick: () => void; danger?: boolean; title?: string; children: React.ReactNode }) {
  return (
    <button type="button" onClick={onClick} title={title} aria-label={title} style={{ minHeight: 28,
      padding: "4px 9px", borderRadius: 6, fontSize: 12, cursor: "pointer",
      border: `1px solid ${danger ? "#ef444433" : "#374151"}`,
      background: danger ? "rgba(239,68,68,0.1)" : "rgba(255,255,255,0.04)",
      color: danger ? "#ef4444" : "#9ca3af",
    }}>
      {children}
    </button>
  );
}

const inputSt: React.CSSProperties = {
  background: "#1a2030", border: "1px solid #374151", borderRadius: 7,
  color: "#f9fafb", fontSize: 13, padding: "8px 10px", outline: "none", width: "100%",
};
const priBtn: React.CSSProperties = {
  background: "#6366f1", color: "#fff", border: "none", borderRadius: 8,
  padding: "8px 20px", fontSize: 13, fontWeight: 600, cursor: "pointer",
};
const secBtn: React.CSSProperties = {
  background: "rgba(255,255,255,0.05)", color: "#9ca3af", border: "1px solid #374151",
  borderRadius: 8, padding: "8px 20px", fontSize: 13, cursor: "pointer",
};
const docBtnStyle: React.CSSProperties = {
  padding: "7px 13px", borderRadius: 8, fontSize: 13, cursor: "pointer",
  background: "rgba(99,102,241,0.1)", color: "#818cf8", border: "1px solid rgba(99,102,241,0.3)",
};

function DocGroup({ title, hint, color, children }: { title: string; hint?: string; color: string; children: React.ReactNode }) {
  return (
    <section aria-label={title}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 7, flexWrap: "wrap" }}>
        <span aria-hidden style={{ width: 8, height: 8, borderRadius: "50%", background: color, flex: "none" }} />
        <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", color: "#9ca3af" }}>{title}</span>
        {hint && <span style={{ fontSize: 12, color: "#6b7280" }}>{hint}</span>}
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>{children}</div>
    </section>
  );
}

function DocAdd({ color, onClick, children }: { color: string; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{ ...docBtnStyle, minHeight: 36, padding: "8px 14px", background: "rgba(255,255,255,0.04)", color: "#e5e7eb", border: "1px solid #374151", borderLeft: `3px solid ${color}` }}
    >
      + {children}
    </button>
  );
}
